<template>
  <div class="computer-mockup">
    <div
      class="monitor-frame"
      :class="{ 'hover-enabled': enableHover }"
      :style="{
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        '--rotate-y': `${rotateY}deg`,
      }"
    >
      <div class="monitor-bezel">
        <div class="monitor-screen">
          <slot />
        </div>
      </div>
      <div v-if="showStand" class="monitor-stand">
        <div class="stand-neck"></div>
        <div class="stand-base"></div>
      </div>
      <div v-if="showCamera" class="webcam"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    maxWidth?: string;
    maxHeight?: string;
    enableHover?: boolean;
    rotateDirection?: "left" | "right";
    showStand?: boolean;
    showCamera?: boolean;
  }>(),
  {
    maxWidth: "900px",
    maxHeight: "650px",
    enableHover: false,
    rotateDirection: "left",
    showStand: true,
    showCamera: true,
  },
);

const rotateY = computed(() => (props.rotateDirection === "left" ? -3 : 3));
</script>

<style scoped>
.computer-mockup {
  display: flex;
  justify-content: center;
  align-items: center;
}

.monitor-frame {
  position: relative;
  width: fit-content;
  height: fit-content;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.monitor-frame.hover-enabled {
  transform: perspective(1500px) rotateY(0deg) rotateX(0deg) translateZ(0px);
  transition:
    transform 0.6s ease,
    filter 0.6s ease;
}

.monitor-frame.hover-enabled:hover {
  transform: perspective(1500px) rotateY(var(--rotate-y)) rotateX(1deg)
    translateZ(40px);
}

.monitor-bezel {
  position: relative;
  background: linear-gradient(
    145deg,
    #d1d5db 0%,
    #b8bec7 25%,
    #9ca3af 60%,
    #cfd5dd 100%
  );
  border-radius: 12px;
  padding: 8px;
  box-shadow:
    0 45px 90px rgba(0, 0, 0, 0),
    inset -3px -3px 6px rgba(0, 0, 0, 0.22),
    inset 3px 3px 6px rgba(255, 255, 255, 0.4),
    inset 0 0 1px rgba(255, 255, 255, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  background-clip: padding-box;
  transition: box-shadow 0.6s ease;
}

.monitor-frame.hover-enabled:hover .monitor-bezel {
  box-shadow:
    0 45px 90px rgba(0, 0, 0, 0.35),
    inset -3px -3px 6px rgba(0, 0, 0, 0.22),
    inset 3px 3px 6px rgba(255, 255, 255, 0.4),
    inset 0 0 1px rgba(255, 255, 255, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.2);
}

.monitor-bezel::before {
  content: "";
  position: absolute;
  top: 6px;
  left: 6px;
  right: 6px;
  height: 80px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.4) 0%,
    rgba(255, 255, 255, 0.15) 50%,
    transparent 100%
  );
  border-radius: 8px;
  pointer-events: none;
  z-index: 1;
}

.monitor-screen {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  box-shadow:
    inset 0 0 20px rgba(0, 0, 0, 0.6),
    0 0 0 1px rgba(255, 255, 255, 0.15);
  display: flex;
}

.monitor-screen :deep(img) {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.webcam {
  position: absolute;
  top: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 8px;
  background: radial-gradient(circle, #1a202c 0%, #0f1419 70%);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow:
    inset 0 1px 3px rgba(0, 0, 0, 0.8),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  z-index: 2;
}

.webcam::after {
  content: "";
  position: absolute;
  top: 1px;
  left: 1px;
  width: 3px;
  height: 3px;
  background: radial-gradient(
    circle at 30% 30%,
    rgba(100, 149, 237, 0.4) 0%,
    transparent 70%
  );
  border-radius: 50%;
}

.monitor-stand {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stand-neck {
  width: 50px;
  height: 34px;
  background: linear-gradient(180deg, #b8bec7 0%, #9ca3af 40%, #c7ced7 100%);
  box-shadow:
    inset 2px 0 4px rgba(255, 255, 255, 0.3),
    inset -2px 0 4px rgba(0, 0, 0, 0.18),
    2px 0 6px rgba(0, 0, 0, 0.16),
    -2px 0 6px rgba(0, 0, 0, 0.16);
  border-radius: 0 0 4px 4px;
}

.stand-base {
  width: 180px;
  height: 14px;
  background: linear-gradient(
    145deg,
    #d1d5db 0%,
    #b8bec7 25%,
    #9ca3af 70%,
    #cfd5dd 100%
  );
  border-radius: 100px;
  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.25),
    inset 0 2px 4px rgba(255, 255, 255, 0.3),
    inset 0 -2px 4px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  margin-top: -4px;
}

.stand-base::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 6px;
  background: radial-gradient(
    ellipse,
    rgba(255, 255, 255, 0.25) 0%,
    transparent 70%
  );
  border-radius: 50%;
}
</style>
